import { useMemo } from 'react'

import { useAffiliateEntry } from '@/hooks/use-affiliate-entry'
import { useHasFeatureAccess } from '@/hooks/use-has-feature-access'
import { useShouldGenerateNfe } from '@/hooks/use-should-generate-nfe'
import type { PlanType } from '@/lib/plans'
import { PLANS } from '@/lib/plans'

import type { QuickActionItem, SearchablePageItem } from '../search-items'
import { quickActions, searchablePages } from '../search-items'

import { useSearchPermissions } from './use-search-permissions'

function hasPermission(
  item: SearchablePageItem,
  isAdmin: boolean,
  isManaged: boolean,
  isManaging: boolean,
  isAffiliateVisible: boolean,
  shouldGenerateNfe: boolean,
): boolean {
  if (item.hiddenWhileManaging && isManaging) return false
  // Checked before the role gates: no NFe means the page does not exist for this
  // account at all, whoever is asking.
  if (item.requiresNfe && !shouldGenerateNfe) return false
  if (item.requiresAffiliateProgram) return isAffiliateVisible
  if (item.requiresAdminAndNotManaged) return isAdmin && !isManaged
  if (item.requiresAdmin) return isAdmin
  return true
}

function hasPlanVisibility(
  item: SearchablePageItem | QuickActionItem,
  planAccess: Record<PlanType, boolean>,
  isManaged: boolean,
): boolean {
  if (!item.minimumPlan) return true
  return planAccess[item.minimumPlan] || !isManaged
}

function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

const NESTED_PATH_PREFIXES = ['/settings/', '/reports/', '/affiliate/']

function isNested(item: SearchablePageItem): boolean {
  return NESTED_PATH_PREFIXES.some((prefix) => item.path.startsWith(prefix))
}

function matchesQuery(label: string, query: string): boolean {
  if (!query) return true
  return normalize(label).includes(normalize(query))
}

export function useFilteredPages(
  query: string,
  t: (key: string) => string,
): {
  filteredPages: Array<SearchablePageItem>
  filteredActions: Array<QuickActionItem>
  shortcuts: Record<string, string>
} {
  const { isAdmin, isManaged, isManaging } = useSearchPermissions()
  // Same gate as the sidebar entry, so the affiliate program appears in search
  // exactly when it appears in the nav (build flag + admin + not-managed +
  // not-managing + not-blocked). Reuses the cached dashboard read.
  const { isVisible: isAffiliateVisible } = useAffiliateEntry()
  const shouldGenerateNfe = useShouldGenerateNfe()
  // Access can't be resolved per item (hooks can't run in a loop), so every
  // plan tier is resolved up front and looked up by the item's minimumPlan.
  const hasOneAccess = useHasFeatureAccess(PLANS.ONE)
  const hasProAccess = useHasFeatureAccess(PLANS.PRO)
  const hasMaxAccess = useHasFeatureAccess(PLANS.MAX)

  const term = query.trim()

  const planAccess = useMemo<Record<PlanType, boolean>>(
    () => ({ One: hasOneAccess, Pro: hasProAccess, Max: hasMaxAccess }),
    [hasOneAccess, hasProAccess, hasMaxAccess],
  )

  const permittedPages = useMemo(
    () =>
      searchablePages.filter(
        (item) =>
          hasPermission(
            item,
            isAdmin,
            isManaged,
            isManaging,
            isAffiliateVisible,
            shouldGenerateNfe,
          ) && hasPlanVisibility(item, planAccess, isManaged),
      ),
    [
      isAdmin,
      isManaged,
      isManaging,
      isAffiliateVisible,
      shouldGenerateNfe,
      planAccess,
    ],
  )

  const filteredPages = useMemo(
    () =>
      permittedPages.filter((item) =>
        term ? matchesQuery(t(item.translationKey), term) : !isNested(item),
      ),
    [permittedPages, term, t],
  )

  const shortcuts = useMemo(
    () =>
      Object.fromEntries(
        permittedPages
          .filter((item) => item.shortcutKey)
          .map((item) => [item.shortcutKey, item.path]),
      ),
    [permittedPages],
  )

  const filteredActions = useMemo(
    () =>
      quickActions.filter(
        (item) =>
          hasPlanVisibility(item, planAccess, isManaged) &&
          matchesQuery(t(item.translationKey), term),
      ),
    [term, t, isManaged, planAccess],
  )

  return { filteredPages, filteredActions, shortcuts }
}
